import type { ReactElement } from "react"
import { useMemo, useState } from "react"

import { useGetAvailableAlternates } from "../../hooks/useGetAvailableAlternates"

import SearchSuggestion from "@/components/SearchSuggestion"
import { DEBOUNCE_DELAY_MS } from "@/constants/common"
import type { AutocompleteOption } from "@/types/form-fields"
import useDebounce from "@/utils/useDebounce"

interface AlternativeProductSearchSuggestionProps {
  companyProductAssociationLocationId: string
  searchInputValue: string
  onInputValueChange: (value: string) => void
  onSearch: (value: string | AutocompleteOption) => void
}

export const AlternativeProductSearchSuggestion = ({
  companyProductAssociationLocationId,
  searchInputValue,
  onInputValueChange,
  onSearch,
}: AlternativeProductSearchSuggestionProps): ReactElement => {
  const [inputValue, setInputValue] = useState(searchInputValue)
  const debouncedQuery = useDebounce(inputValue, DEBOUNCE_DELAY_MS)

  const { availableAlternates, isFetching } = useGetAvailableAlternates({
    companyProductAssociationLocationId,
    searchTerm: debouncedQuery,
    pause: debouncedQuery.length < 2,
  })

  const options = useMemo<AutocompleteOption[]>(
    () =>
      availableAlternates.map((alt) => ({
        label: alt.product!.companyProductDescription ?? `Product ${alt.productId}`,
        id: alt.alternateCompanyProductAssociationLocationId,
      })),
    [availableAlternates]
  )

  const handleSetInputValue = (value: string) => {
    setInputValue(value)
    onInputValueChange(value)
    if (!value) onSearch("")
  }

  return (
    <SearchSuggestion
      onSearch={onSearch}
      placeholder='Search by Product Name or Product ID'
      inputValue={inputValue}
      setInputValue={handleSetInputValue}
      options={options}
      fetching={isFetching}
    />
  )
}
